'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

/**
 * 解析URL中的base64编码参数
 * @param paramName URL参数名，默认为'params'
 * @param onParamsReceived 参数解析成功后的回调函数
 * @param onError 解析失败时的回调函数
 */
export const useUrlParams = <T = Record<string, unknown>>(
  paramName = 'params',
  onParamsReceived?: (params: T) => void,
  onError?: (error: Error) => void,
) => {
  const searchParams = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // 避免重复处理
    if (hasProcessed.current) return;

    const encodedParams = searchParams.get(paramName);
    if (!encodedParams) return;

    try {
      // base64解码并解析JSON
      const decodedString = atob(encodedParams);
      const params = JSON.parse(decodedString) as T;

      onParamsReceived?.(params);
      hasProcessed.current = true;
    } catch (error) {
      const parseError = new Error(
        `Failed to parse URL parameters: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
      onError?.(parseError);
    }
  }, [searchParams, paramName, onParamsReceived, onError]);

  // 重置处理状态
  const reset = () => {
    hasProcessed.current = false;
  };

  return { reset };
};
